import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { userApi } from "./api";
import { clientId } from "./client-id";
import { notifyCreditActionSubmitted, requestCreditSpend } from "./credit-interactions";
import {
  MAX_ADVANCED_BEAUTIFY_REGIONS,
  appendRegionInstructionLine,
  parseRegionInstructions,
  removeAndRenumberRegions,
  removeRegionInstructionLine,
  type AdvancedBeautifyRegion,
} from "./advanced-beautify-annotation-utils";
import {
  IMAGE2_REVISION_INSTRUCTION_MAX_LENGTH,
  image2RevisionInstructionLength,
  image2RevisionInstructionValid,
  limitImage2RevisionInstruction,
} from "./image2-revision-instruction";
import type { ProjectRender } from "./project-types";

export type Image2RevisionJobStatus = "validating" | "submitting" | "generating" | "undoing";

export interface Image2RevisionRegionInput {
  number: number;
  x: number;
  y: number;
  width: number;
  height: number;
  instruction: string;
}

export interface Image2RevisionJob {
  slideIndex: number;
  instruction: string;
  status: Image2RevisionJobStatus;
  idempotencyKey: string;
  regions?: Image2RevisionRegionInput[];
}

export interface Image2RevisionFeedback {
  tone: "error" | "success";
  text: string;
}

export interface PptEditMessage {
  id: string;
  role: "user" | "assistant" | "system";
  messageType: string;
  contentText: string;
  sourceRenderId?: string;
  resource?: { type: string; slideIndex?: number; requestMessageId?: string; targetRevisionMessageId?: string };
  createdAt: string;
  expiresAt: string;
  resourceExpired: boolean;
  resourceDeleted: boolean;
  canRollback: boolean;
}

interface PptEditMessagesResponse {
  ok: true;
  messages: PptEditMessage[];
  nextBefore?: string;
}

interface Image2RenderResponse {
  ok: true;
  queued?: boolean;
  requestMessage?: PptEditMessage;
  progressMessage?: PptEditMessage;
}

interface RevisionInstructionCheckResponse {
  ok: true;
  reasonable: boolean;
  validationToken?: string;
  expiresAt?: string;
}

export interface Image2RevisionSession {
  renderId: string;
  selectedPage: number;
  messages: PptEditMessage[];
  pageMessages: PptEditMessage[];
  draft: string;
  regions: AdvancedBeautifyRegion[];
  annotationMode: boolean;
  job?: Image2RevisionJob;
  feedback?: Image2RevisionFeedback;
  processing: boolean;
  canEdit: boolean;
  instructionValid: boolean;
  setDraft: (value: string) => void;
  setAnnotationMode: (value: boolean | ((current: boolean) => boolean)) => void;
  addRegion: (region: AdvancedBeautifyRegion) => void;
  removeRegion: (region: AdvancedBeautifyRegion) => void;
  clearRegions: () => void;
  resetAll: () => void;
  submit: () => Promise<void>;
  rollback: (message: PptEditMessage) => Promise<void>;
  reloadMessages: () => Promise<PptEditMessage[]>;
}

export function useImage2RevisionSession({
  render,
  selectedPage,
  enabled,
  idempotencyPrefix,
  onRefresh,
  onSelectPage,
  initialAnnotationMode = false,
  preserveMessagesAcrossRenders = false,
}: {
  render?: ProjectRender;
  selectedPage: number;
  enabled: boolean;
  idempotencyPrefix: string;
  onRefresh: () => Promise<unknown>;
  onSelectPage?: (page: number) => void;
  initialAnnotationMode?: boolean;
  preserveMessagesAcrossRenders?: boolean;
}): Image2RevisionSession {
  const page = Math.max(1, Math.floor(selectedPage || 1));
  const renderId = editableImage2RenderId(render);
  const [messages, setMessages] = useState<PptEditMessage[]>([]);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [jobs, setJobs] = useState<Record<number, Image2RevisionJob>>({});
  const [feedbacks, setFeedbacks] = useState<Record<number, Image2RevisionFeedback>>({});
  const [regionsByPage, setRegionsByPage] = useState<Record<number, AdvancedBeautifyRegion[]>>({});
  const [annotationMode, setAnnotationMode] = useState(initialAnnotationMode);
  const activePages = useRef(new Set<number>());
  const messageLoadSequence = useRef(0);
  const previousRenderId = useRef("");
  const onRefreshRef = useRef(onRefresh);
  const onSelectPageRef = useRef(onSelectPage);
  onRefreshRef.current = onRefresh;
  onSelectPageRef.current = onSelectPage;

  const loadMessages = useCallback(async () => {
    const sequence = ++messageLoadSequence.current;
    if (!renderId) {
      setMessages((current) => preserveMessagesAcrossRenders
        ? current.map((message) => ({ ...message, canRollback: false }))
        : []);
      return [];
    }
    const response = await userApi<PptEditMessagesResponse>(
      `/api/app/v2/renders/${encodeURIComponent(renderId)}/edit-messages?limit=100`,
      { cache: "no-store", headers: { "cache-control": "no-cache" } },
    );
    if (sequence === messageLoadSequence.current) {
      const incoming = response.messages.map((message) => ({ ...message, sourceRenderId: renderId }));
      setMessages((current) => preserveMessagesAcrossRenders
        ? mergeImage2RevisionMessageHistory(current, incoming, renderId)
        : incoming);
    }
    return response.messages;
  }, [preserveMessagesAcrossRenders, renderId]);

  useEffect(() => {
    if (previousRenderId.current && previousRenderId.current !== renderId) {
      setDrafts({});
      setJobs({});
      setFeedbacks({});
      setRegionsByPage({});
      setAnnotationMode(false);
      activePages.current.clear();
    }
    previousRenderId.current = renderId;
    void loadMessages().catch((caught) => {
      setFeedbacks((current) => ({
        ...current,
        [page]: { tone: "error", text: caught instanceof Error ? caught.message : "修改记录加载失败" },
      }));
    });
  }, [loadMessages, page, renderId]);

  const pageMessages = useMemo(
    () => messages.filter((message) =>
      message.role !== "system"
      && (!message.resource?.slideIndex || message.resource.slideIndex === page)
    ),
    [messages, page],
  );
  const persistentPending = pageMessages.some((message) => message.messageType === "revision_progress");
  const job = jobs[page];
  const processing = Boolean(job) || persistentPending;
  const draft = drafts[page] || "";
  const regions = regionsByPage[page] || [];
  const supportsPageRevision = Boolean(
    render?.outputManifest?.editCapabilities.includes("image_regenerate")
    || render?.outputManifest?.slides.some((slide) =>
      slide.index === page && slide.editableType === "image_regenerate"
    ),
  );
  const canEdit = enabled && Boolean(renderId && supportsPageRevision) && !processing;

  const setDraft = useCallback((value: string) => {
    setDrafts((current) => ({ ...current, [page]: limitImage2RevisionInstruction(value) }));
    setFeedbacks((current) => omitPageRecord(current, page));
  }, [page]);

  const addRegion = useCallback((region: AdvancedBeautifyRegion) => {
    const currentRegions = regionsByPage[page] || [];
    if (currentRegions.length >= MAX_ADVANCED_BEAUTIFY_REGIONS) return;
    const nextDraft = appendRegionInstructionLine(drafts[page] || "", region.number);
    if (image2RevisionInstructionLength(nextDraft) > IMAGE2_REVISION_INSTRUCTION_MAX_LENGTH) {
      setFeedbacks((current) => ({
        ...current,
        [page]: { tone: "error", text: "修改要求已达到 1000 字上限，请精简内容后再增加画框" },
      }));
      return;
    }
    setRegionsByPage((current) => ({ ...current, [page]: [...(current[page] || []), region] }));
    setDrafts((current) => ({ ...current, [page]: nextDraft }));
    setFeedbacks((current) => omitPageRecord(current, page));
  }, [drafts, page, regionsByPage]);

  const removeRegion = useCallback((removed: AdvancedBeautifyRegion) => {
    setRegionsByPage((current) => ({
      ...current,
      [page]: removeAndRenumberRegions(current[page] || [], removed.id),
    }));
    setDrafts((current) => ({
      ...current,
      [page]: removeRegionInstructionLine(current[page] || "", removed.number),
    }));
    setFeedbacks((current) => omitPageRecord(current, page));
  }, [page]);

  const clearRegions = useCallback(() => {
    setRegionsByPage((current) => omitPageRecord(current, page));
    setDrafts((current) => ({ ...current, [page]: "" }));
    setFeedbacks((current) => omitPageRecord(current, page));
  }, [page]);

  const resetAll = useCallback(() => {
    setDrafts({});
    setJobs({});
    setFeedbacks({});
    setRegionsByPage({});
    setAnnotationMode(false);
    activePages.current.clear();
  }, []);

  const submit = useCallback(async () => {
    const instruction = String(drafts[page] || "").trim();
    const source = render;
    if (!enabled || !renderId || !source?.outputManifest?.slides.some((slide) => slide.index === page)
      || activePages.current.has(page)) return;
    const prepared = buildImage2RevisionRegions(instruction, regionsByPage[page] || []);
    if (!prepared.ok) {
      setFeedbacks((current) => ({ ...current, [page]: { tone: "error", text: prepared.error } }));
      return;
    }
    if (!image2RevisionInstructionValid(instruction)) {
      setFeedbacks((current) => ({
        ...current,
        [page]: { tone: "error", text: "修改要求需输入 7–1000 个字符" },
      }));
      return;
    }

    const baseJob: Image2RevisionJob = {
      slideIndex: page,
      instruction,
      status: "validating",
      idempotencyKey: `${idempotencyPrefix}-revision-${renderId}-${page}-${clientId()}`,
      ...(prepared.regions?.length ? { regions: prepared.regions } : {}),
    };
    activePages.current.add(page);
    setFeedbacks((current) => omitPageRecord(current, page));
    setJobs((current) => ({ ...current, [page]: baseJob }));
    const previousIds = new Set(messages.map((message) => message.id));
    try {
      const checked = await userApi<RevisionInstructionCheckResponse>(
        `/api/app/v2/renders/${encodeURIComponent(renderId)}/revision-instruction-check`,
        {
          method: "POST",
          body: JSON.stringify({
            slideIndex: page,
            instruction,
            ...(prepared.regions?.length ? { regions: prepared.regions } : {}),
          }),
        },
      );
      if (!checked.reasonable || !checked.validationToken) {
        setFeedbacks((current) => ({
          ...current,
          [page]: { tone: "error", text: "请重新输入更明确、可执行的页面修改要求" },
        }));
        return;
      }
      const approved = await requestCreditSpend({
        operation: "image2_revision",
        slideCount: 1,
      });
      if (!approved) return;

      setJobs((current) => ({ ...current, [page]: { ...baseJob, status: "submitting" } }));
      const response = await userApi<Image2RenderResponse>(
        `/api/app/v2/renders/${encodeURIComponent(renderId)}/revise-slide`,
        {
          method: "POST",
          body: JSON.stringify({
            slideIndex: page,
            instruction,
            idempotencyKey: baseJob.idempotencyKey,
            instructionValidationToken: checked.validationToken,
            ...(prepared.regions?.length ? { regions: prepared.regions } : {}),
          }),
        },
      );
      notifyCreditActionSubmitted();
      setMessages((current) => appendUniqueMessages(current, response.requestMessage, response.progressMessage));
      if (response.queued) {
        setJobs((current) => ({ ...current, [page]: { ...baseJob, status: "generating" } }));
        await waitForAppliedRevision(renderId, page, response.requestMessage?.id, previousIds, setMessages);
      } else {
        await loadMessages();
      }
      setFeedbacks((current) => ({
        ...current,
        [page]: {
          tone: "success",
          text: prepared.regions?.length
            ? `第 ${page} 页框选修改已应用，共处理 ${prepared.regions.length} 个区域，可在本条消息中回退。`
            : `第 ${page} 页重做已应用，可在本条消息中回退。`,
        },
      }));
      setDrafts((current) => ({ ...current, [page]: "" }));
      setRegionsByPage((current) => omitPageRecord(current, page));
      if (prepared.regions?.length) setAnnotationMode(false);
      await onRefreshRef.current();
    } catch (caught) {
      setFeedbacks((current) => ({
        ...current,
        [page]: { tone: "error", text: caught instanceof Error ? caught.message : "页面修改失败，请稍后重试" },
      }));
    } finally {
      await loadMessages().catch(() => undefined);
      setJobs((current) => omitPageRecord(current, page));
      activePages.current.delete(page);
    }
  }, [drafts, enabled, idempotencyPrefix, loadMessages, messages, page, regionsByPage, render, renderId]);

  const rollback = useCallback(async (message: PptEditMessage) => {
    const targetPage = Math.max(1, Math.floor(message.resource?.slideIndex || page));
    if (!renderId || !message.canRollback || activePages.current.has(targetPage)) return;
    onSelectPageRef.current?.(targetPage);
    activePages.current.add(targetPage);
    setFeedbacks((current) => omitPageRecord(current, targetPage));
    setJobs((current) => ({
      ...current,
      [targetPage]: {
        slideIndex: targetPage,
        instruction: "",
        status: "undoing",
        idempotencyKey: `${idempotencyPrefix}-rollback-${renderId}-${targetPage}-${clientId()}`,
      },
    }));
    try {
      const restoreInitial = message.messageType === "initial_version";
      await userApi(
        restoreInitial
          ? `/api/app/v2/renders/${encodeURIComponent(renderId)}/edit-messages/${encodeURIComponent(message.id)}/rollback`
          : `/api/app/v2/renders/${encodeURIComponent(renderId)}/undo-slide-revision`,
        {
          method: "POST",
          body: JSON.stringify(restoreInitial ? {} : { slideIndex: targetPage }),
        },
      );
      setFeedbacks((current) => ({
        ...current,
        [targetPage]: {
          tone: "success",
          text: restoreInitial
            ? `第 ${targetPage} 页已恢复到首次生成版本。`
            : `第 ${targetPage} 页已回退到上一个版本。`,
        },
      }));
      setDrafts((current) => ({ ...current, [targetPage]: "" }));
      setRegionsByPage((current) => omitPageRecord(current, targetPage));
      await Promise.all([loadMessages(), onRefreshRef.current()]);
    } catch (caught) {
      setFeedbacks((current) => ({
        ...current,
        [targetPage]: { tone: "error", text: caught instanceof Error ? caught.message : "页面版本回退失败" },
      }));
    } finally {
      setJobs((current) => omitPageRecord(current, targetPage));
      activePages.current.delete(targetPage);
    }
  }, [idempotencyPrefix, loadMessages, page, renderId]);

  return {
    renderId,
    selectedPage: page,
    messages,
    pageMessages,
    draft,
    regions,
    annotationMode,
    job,
    feedback: feedbacks[page],
    processing,
    canEdit,
    instructionValid: image2RevisionInstructionValid(draft),
    setDraft,
    setAnnotationMode,
    addRegion,
    removeRegion,
    clearRegions,
    resetAll,
    submit,
    rollback,
    reloadMessages: loadMessages,
  };
}

export function buildImage2RevisionRegions(
  instruction: string,
  regions: readonly AdvancedBeautifyRegion[],
): { ok: true; regions?: Image2RevisionRegionInput[] } | { ok: false; error: string } {
  if (!regions.length) return { ok: true };
  const parsed = parseRegionInstructions(instruction, regions.length);
  if (!parsed.ok) return parsed;
  return {
    ok: true,
    regions: regions.map((region, index) => ({
      number: index + 1,
      x: region.x,
      y: region.y,
      width: region.width,
      height: region.height,
      instruction: parsed.instructions[index].instruction,
    })),
  };
}

export function editableImage2RenderId(render?: ProjectRender): string {
  if (!render
    || render.renderer !== "image2"
    || render.status !== "succeeded"
    || render.productCode === "image2-slide-revision-preview"
    || render.outputManifest?.status !== "succeeded") {
    return "";
  }
  const editable = render.outputManifest.editCapabilities.includes("image_regenerate")
    || render.outputManifest.slides.some((slide) => slide.editableType === "image_regenerate");
  return editable ? render.id : "";
}

export function image2RevisionJobMessage(status: Image2RevisionJobStatus, page: number): string {
  switch (status) {
    case "validating":
      return `正在检测第 ${page} 页的修改要求`;
    case "submitting":
      return `正在提交第 ${page} 页的重做任务`;
    case "generating":
      return `正在重新生成第 ${page} 页`;
    case "undoing":
      return `正在回退第 ${page} 页的页面版本`;
  }
}

export function latestRollbackVersionBySlide(messages: readonly PptEditMessage[]): Map<number, string> {
  const result = new Map<number, string>();
  for (const message of messages) {
    if (["revision", "rollback"].includes(message.messageType) && message.resource?.slideIndex) {
      result.set(message.resource.slideIndex, message.id);
    }
  }
  return result;
}

export function visibleImage2RevisionMessages<T extends { role: string }>(messages: readonly T[]): T[] {
  return messages.filter((message) => message.role !== "system");
}

export function mergeImage2RevisionMessageHistory(
  current: readonly PptEditMessage[],
  incoming: readonly PptEditMessage[],
  activeRenderId: string,
): PptEditMessage[] {
  const byId = new Map(current.map((message) => [message.id, message]));
  for (const message of incoming) byId.set(message.id, message);
  return [...byId.values()]
    .map((message) => message.sourceRenderId && message.sourceRenderId !== activeRenderId
      ? { ...message, canRollback: false }
      : message)
    .sort((left, right) => {
      const timeDifference = Date.parse(left.createdAt) - Date.parse(right.createdAt);
      return Number.isFinite(timeDifference) && timeDifference !== 0
        ? timeDifference
        : left.id.localeCompare(right.id);
    });
}

function appendUniqueMessages(
  current: PptEditMessage[],
  ...incoming: Array<PptEditMessage | undefined>
): PptEditMessage[] {
  const next = [...current];
  for (const message of incoming) {
    if (message && !next.some((item) => item.id === message.id)) next.push(message);
  }
  return next;
}

function omitPageRecord<T>(record: Record<number, T>, page: number): Record<number, T> {
  const next = { ...record };
  delete next[page];
  return next;
}

async function waitForAppliedRevision(
  renderId: string,
  slideIndex: number,
  requestMessageId: string | undefined,
  previousIds: Set<string>,
  onMessages: (messages: PptEditMessage[]) => void,
): Promise<void> {
  for (let attempt = 0; attempt < 240; attempt += 1) {
    await new Promise((resolve) => window.setTimeout(resolve, 2500));
    const response = await userApi<PptEditMessagesResponse>(
      `/api/app/v2/renders/${encodeURIComponent(renderId)}/edit-messages?limit=100`,
      { cache: "no-store", headers: { "cache-control": "no-cache" } },
    );
    onMessages(response.messages);
    const candidates = response.messages.filter((message) =>
      !previousIds.has(message.id)
      && message.role === "assistant"
      && message.resource?.slideIndex === slideIndex
      && (!requestMessageId || message.resource.requestMessageId === requestMessageId)
    );
    const failed = [...candidates].reverse().find((message) => message.messageType === "error");
    if (failed) throw new Error(failed.contentText || "当前页面修改失败");
    if ([...candidates].reverse().some((message) => message.messageType === "revision")) return;
  }
  throw new Error("当前页面修改等待超时，请稍后刷新项目查看结果");
}
